// Banner de instalação do PWA (Progressive Web App)
// - Android/Chrome/Edge: usa o evento nativo `beforeinstallprompt`
// - iOS (Safari): mostra instruções manuais (iOS não suporta install programático)
import React, { useEffect, useState } from 'react';
import {
    Banner,
    Inner,
    IconWrap,
    TextBlock,
    Title,
    Subtitle,
    Actions,
    InstallButton,
    DismissButton,
    IosHint
} from './styled';

const DISMISS_KEY = 'pwa_install_dismissed';

// Detecta iOS (iPhone/iPad)
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// Detecta se já está rodando instalado (standalone)
const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

const PWAInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [visible, setVisible] = useState(false);
    const [showIosHint, setShowIosHint] = useState(false);

    useEffect(() => {
        // Não mostra se já está instalado ou se o usuário já dispensou
        if (isStandalone()) return;
        if (localStorage.getItem(DISMISS_KEY) === 'true') return;
        // Evita sobreposição com o banner de cookies: só aparece após decisão de cookies
        if (!localStorage.getItem('cookie_consent')) return;

        // Android / Chrome / Edge
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setVisible(true);
        };
        window.addEventListener('beforeinstallprompt', handler);

        // iOS: não dispara beforeinstallprompt — mostramos instruções
        if (isIOS()) {
            // Pequeno atraso para não atrapalhar o primeiro carregamento
            const t = setTimeout(() => setVisible(true), 2500);
            return () => {
                clearTimeout(t);
                window.removeEventListener('beforeinstallprompt', handler);
            };
        }

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, 'true');
        setVisible(false);
    };

    const handleInstall = async () => {
        if (isIOS()) {
            // Em iOS, apenas alterna as instruções
            setShowIosHint((v) => !v);
            return;
        }
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted' || outcome === 'dismissed') {
            setDeferredPrompt(null);
            setVisible(false);
            if (outcome === 'accepted') {
                localStorage.setItem(DISMISS_KEY, 'true');
            }
        }
    };

    if (!visible) return null;

    return (
        <Banner role="dialog" aria-label="Instalar aplicativo TiqChat">
            <Inner>
                <IconWrap>
                    <img src="/logo192.png" alt="TiqChat" width="44" height="44" />
                </IconWrap>
                <TextBlock>
                    <Title>Instale o TiqChat no seu celular</Title>
                    <Subtitle>
                        Acesse mais rápido, direto da tela inicial — como um aplicativo.
                    </Subtitle>
                    {showIosHint && (
                        <IosHint>
                            No iPhone: toque em <strong>Compartilhar</strong> (ícone de seta para cima)
                            e depois em <strong>"Adicionar à Tela de Início"</strong>.
                        </IosHint>
                    )}
                </TextBlock>
                <Actions>
                    <DismissButton onClick={dismiss}>Agora não</DismissButton>
                    <InstallButton onClick={handleInstall}>
                        {isIOS() ? 'Como instalar' : 'Instalar'}
                    </InstallButton>
                </Actions>
            </Inner>
        </Banner>
    );
};

export default PWAInstallPrompt;
